import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  InjectionToken,
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { AzureADConfig } from '../common/public-api';
import { SIGVCoreModule } from '../core/public-api';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1; // Remove this line to use Angular Universal

const AZURE_AD_CONFIG = new InjectionToken<AzureADConfig>('AZURE_AD_CONFIG');

export function loggerCallback(logLevel: LogLevel, message: string): void {
  console.log(message);
}
export function MSALInstanceFactory(
  config: AzureADConfig
): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: config?.auth.clientId,
      authority: config?.auth.authority,
      redirectUri: config?.auth.redirectUri,
      postLogoutRedirectUri: config?.auth.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11. Remove this line to use Angular Universal
    },
    system: {
      iframeHashTimeout: 60000,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(
  config: AzureADConfig
): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(config.resourceMap.uri, config.resourceMap.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(
  config: AzureADConfig
): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      redirectUri: config.auth.redirectUri,
      scopes: config.resourceMap.scopes,
    },
    loginFailedRoute: '/login-failed',
  };
}

@NgModule({
  declarations: [],
  imports: [MsalModule, SIGVCoreModule],
  providers: [],
})
export class SIGVAzureADModule {
  constructor(@Optional() @SkipSelf() parentModule: SIGVAzureADModule) {
    if (parentModule) {
      throw new Error(
        `${
          new.target.name
        } is already loaded. Import in your base AppModule only.`
      );
    }
  }

  static forRoot(
    config: AzureADConfig
  ): ModuleWithProviders<SIGVAzureADModule> {
    return {
      ngModule: SIGVAzureADModule,
      providers: [
        {
          provide: AZURE_AD_CONFIG,
          useValue: config,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: MsalInterceptor,
          multi: true,
        },
        {
          provide: MSAL_INSTANCE,
          useFactory: MSALInstanceFactory,
          deps: [AZURE_AD_CONFIG],
        },
        {
          provide: MSAL_GUARD_CONFIG,
          useFactory: MSALGuardConfigFactory,
          deps: [AZURE_AD_CONFIG],
        },
        {
          provide: MSAL_INTERCEPTOR_CONFIG,
          useFactory: MSALInterceptorConfigFactory,
          deps: [AZURE_AD_CONFIG],
        },
      ],
    };
  }
}
